import { Building, Check, Globe, Plus } from "lucide-mui";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Skeleton from "@mui/material/Skeleton";
import { useState } from "react";
import { ORG_ADMIN_SCOPE } from "../../auth/orgRoutes";
import { userHasOrgScope } from "../../auth/scopes";
import ContextRow from "../../components/ContextRow";
import TenantAvatar from "../../components/TenantAvatar";
import TenantDialog from "../../components/TenantDialog";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useTenant } from "../../contexts/TenantContext";
import { flyoutListSx } from "./styles";

const FLYOUT_ID = "workspace";
const AVATAR_SIZE = 28;

interface WorkspaceSwitcherProps {
  collapsed: boolean;
  pinnedFlyoutId: string | null;
  setPinnedFlyoutId: (id: string | null) => void;
}

/** The org + tenant the app is working in: switch either, or add a tenant. */
export default function WorkspaceSwitcher({ collapsed, pinnedFlyoutId, setPinnedFlyoutId }: WorkspaceSwitcherProps) {
  const { user } = useAuth();
  const { selectedOrgId, setSelectedOrgId, organizations, currentOrg } = useOrganization();
  const { selectedTenantId, setSelectedTenantId, selectedTenant, tenants, isLoading } = useTenant();
  const [addTenantOpen, setAddTenantOpen] = useState(false);

  const close = () => setPinnedFlyoutId(null);
  const orgName = currentOrg?.name ?? organizations[0]?.name;
  const canManageOrg = userHasOrgScope(user, selectedOrgId, ORG_ADMIN_SCOPE);

  const icon = selectedTenant ? (
    <TenantAvatar name={selectedTenant.name} logoUrl={selectedTenant.logo_url} size={AVATAR_SIZE} />
  ) : (
    <Box
      sx={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: 1,
        display: "grid",
        placeItems: "center",
        bgcolor: "action.selected",
        color: "text.secondary",
      }}
    >
      <Globe sx={{ fontSize: 16 }} />
    </Box>
  );

  const flyout = (
    <List disablePadding sx={flyoutListSx}>
      <ListSubheader disableSticky>Tenants</ListSubheader>
      {isLoading
        ? Array.from({ length: 3 }).map((_, i) => (
            <ListItemButton key={`tenant-skel-${i}`} disabled>
              <Skeleton variant="rounded" width={20} height={20} />
              <Skeleton variant="text" width="70%" />
            </ListItemButton>
          ))
        : tenants.length === 0 && (
            <ListItemButton disabled>
              <ListItemIcon>
                <Globe />
              </ListItemIcon>
              <ListItemText primary="No tenant" />
            </ListItemButton>
          )}
      {tenants.map((t) => {
        const selected = t.id === selectedTenantId;
        return (
          <ListItemButton
            key={t.id}
            selected={selected}
            onClick={() => {
              setSelectedTenantId(t.id);
              close();
            }}
          >
            <ListItemIcon>
              <TenantAvatar name={t.name} logoUrl={t.logo_url} size={20} />
            </ListItemIcon>
            <ListItemText primary={t.name} />
            {selected && <Check sx={{ fontSize: 16 }} color="primary" />}
          </ListItemButton>
        );
      })}
      {(canManageOrg || (!isLoading && tenants.length === 0)) && (
        <ListItemButton
          onClick={() => {
            setAddTenantOpen(true);
            close();
          }}
        >
          <ListItemIcon>
            <Plus />
          </ListItemIcon>
          <ListItemText primary="Add tenant" />
        </ListItemButton>
      )}

      {organizations.length > 1 && (
        <>
          <Divider />
          <ListSubheader disableSticky>Organization</ListSubheader>
          {organizations.map((org) => {
            const selected = org.id === selectedOrgId;
            return (
              <ListItemButton
                key={org.id}
                selected={selected}
                onClick={() => {
                  setSelectedOrgId(org.id);
                  close();
                }}
              >
                <ListItemIcon>
                  <Building />
                </ListItemIcon>
                <ListItemText primary={org.name} />
                {selected && <Check sx={{ fontSize: 16 }} color="primary" />}
              </ListItemButton>
            );
          })}
        </>
      )}

    </List>
  );

  return (
    <>
      <ContextRow
        variant="switcher"
        kicker={orgName ?? "Tenant"}
        label={selectedTenant?.name ?? "No tenant"}
        icon={icon}
        iconSize={AVATAR_SIZE}
        collapsed={collapsed}
        flyoutContent={flyout}
        flyoutId={FLYOUT_ID}
        pinnedFlyoutId={pinnedFlyoutId}
        setPinnedFlyoutId={setPinnedFlyoutId}
        loading={isLoading}
      />
      {addTenantOpen && (
        <TenantDialog onClose={() => setAddTenantOpen(false)} organizationId={selectedOrgId ?? undefined} />
      )}
    </>
  );
}
